import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Rating,
  Chip,
  Divider
} from '@mui/material'
import SEO from '@components/SEO'
import {
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Star as StarIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  PhotoCamera as PhotoIcon,
  CloudUpload as UploadIcon,
  Share as ShareIcon,
  Notifications as NotificationsIcon,
  Timeline as TimelineIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  ArrowForward as ArrowForwardIcon,
  LocalFireDepartment as FireIcon,
  Public as PublicIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material'

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate()

  const mainFeatures = [
    {
      icon: <InventoryIcon sx={{ fontSize: 60, color: '#8B4513' }} />,
      title: 'Collection Management',
      description: 'Comprehensive tools to organize and track your cigar collection across multiple humidors.',
      features: [
        'Multiple humidor support',
        'Detailed cigar specifications',
        'Batch and box tracking',
        'Inventory alerts and notifications',
        'Collection search and filtering',
        'Custom categorization'
      ]
    },
    {
      icon: <StarIcon sx={{ fontSize: 60, color: '#8B4513' }} />,
      title: 'Smoking Journal',
      description: 'Record and track every smoking experience with detailed notes and ratings.',
      features: [
        '5-factor rating system',
        'Text tasting notes',
        'Session image URLs (file upload planned)',
        'Session history and search',
        'Duration tracking',
        'Linked to collection cigars'
      ]
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 60, color: '#8B4513' }} />,
      title: 'Analytics & Insights',
      description: 'Gain valuable insights into your collection and smoking preferences.',
      features: [
        'Current collection value (purchase-price based)',
        'Session analytics and smoking patterns',
        'Preference and frequency insights',
        'Rating trends over time',
        'CSV and JSON export',
        'Value-over-time charts (planned)'
      ]
    }
  ]

  const additionalFeatures = [
    {
      icon: <PhotoIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Session Images',
      description: 'Attach image URLs to smoking sessions today; file uploads planned.'
    },
    {
      icon: <UploadIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Data Import/Export',
      description: 'Import existing collections and export your data in multiple formats.'
    },
    {
      icon: <ShareIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Sharing (planned)',
      description: 'Public sharing of reviews and collection highlights is on the roadmap.'
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Smart Notifications',
      description: 'Get alerts for collection milestones, humidor conditions, and more.'
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Aging Tracking',
      description: 'Track cigar aging and optimal smoking windows for peak enjoyment.'
    },
    {
      icon: <MoneyIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Price Tracking',
      description: 'See current collection value and average price; historical trends planned.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Community Database',
      description: 'Browse and contribute to the shared cigar database; public social feeds planned.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Privacy & Security',
      description: 'Your data is encrypted and secure with complete privacy protection.'
    }
  ]

  const benefits = [
    {
      title: 'For Beginners',
      description: 'Learn about cigar collecting with guided setup and educational content.',
      points: [
        'Step-by-step collection setup',
        'Educational resources and guides',
        'Beginner-friendly interface',
        'Community support and mentorship'
      ]
    },
    {
      title: 'For Enthusiasts',
      description: 'Advanced features for serious collectors who want detailed tracking.',
      points: [
        'Comprehensive analytics and reporting',
        'Advanced collection management',
        'Professional-grade tools',
        'Custom categorization and tagging'
      ]
    },
    {
      title: 'For Professionals',
      description: 'Enterprise features for retailers, reviewers, and industry professionals.',
      points: [
        'API access and integrations',
        'White-label solutions',
        'Custom reporting and analytics',
        'Dedicated support and training'
      ]
    }
  ]

  return (
    <Box>
      <SEO
        title="Features - CigarTrack"
        description="Explore CigarTrack's powerful features: multi-humidor management, smoking session logs, collection analytics, photo uploads, and more. Start your free trial today."
        keywords="cigar app features, humidor management, cigar tracking, smoking journal, collection analytics"
        url="/features"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              Powerful Features for Every Cigar Enthusiast
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              Discover the comprehensive tools and features that make CigarTrack 
              the ultimate platform for cigar collection management and enjoyment.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                backgroundColor: 'white',
                color: '#8B4513',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Try All Features Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Features Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {mainFeatures.map((feature, index) => (
            <Box key={index} sx={{ mb: 12 }}>
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={6} order={{ xs: index % 2 === 0 ? 1 : 2, md: index % 2 === 0 ? 1 : 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {feature.icon}
                      <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                          ml: 2,
                          color: '#8B4513',
                          fontWeight: 600
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        color: 'text.secondary',
                        lineHeight: 1.6
                      }}
                    >
                      {feature.description}
                    </Typography>
                    <List>
                      {feature.features.map((item, itemIndex) => (
                        <ListItem key={itemIndex} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: 'body1',
                              fontWeight: 500
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/register')}
                      sx={{
                        mt: 3,
                        borderColor: '#8B4513',
                        color: '#8B4513',
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#7A3D0F',
                          backgroundColor: 'rgba(139, 69, 19, 0.1)'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: index % 2 === 0 ? 2 : 1, md: index % 2 === 0 ? 2 : 1 }}>
                  <Paper
                    elevation={8}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      minHeight: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    {index === 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                          My Humidor — 24 cigars
                        </Typography>
                        {[
                          { name: 'Padron 1964 Anniversary', brand: 'Padron', country: 'Nicaragua', rating: 5 },
                          { name: 'Cohiba Robusto', brand: 'Cohiba', country: 'Cuba', rating: 4.5 },
                          { name: 'Arturo Fuente OpusX', brand: 'Arturo Fuente', country: 'Dom. Republic', rating: 4 }
                        ].map((cigar, i) => (
                          <Card key={i} sx={{ mb: 1.5, borderLeft: '4px solid #8B4513' }} elevation={1}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="body1" fontWeight={600} sx={{ color: '#3d1f0a', lineHeight: 1.3 }}>
                                {cigar.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Chip label={cigar.brand} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: 'rgba(139,69,19,0.1)', color: '#8B4513' }} />
                                  <Chip label={cigar.country} size="small" sx={{ fontSize: '0.65rem', height: 20 }} variant="outlined" />
                                </Box>
                                <Rating value={cigar.rating} precision={0.5} readOnly size="small" sx={{ color: '#DAA520' }} />
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                    {index === 1 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                          Recent Sessions
                        </Typography>
                        <List disablePadding>
                          {[
                            { date: 'Apr 5, 2026', cigar: 'Padron 1964 Exclusivo', notes: 'Rich cocoa, cedar finish', rating: 5 },
                            { date: 'Mar 28, 2026', cigar: 'Montecristo No. 2', notes: 'Earthy, smooth draw', rating: 4 },
                            { date: 'Mar 20, 2026', cigar: 'Oliva Serie V', notes: 'Peppery, medium body', rating: 4.5 }
                          ].map((session, i) => (
                            <React.Fragment key={i}>
                              <ListItem sx={{ px: 0, py: 1.5, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#3d1f0a' }}>
                                      {session.cigar}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                      {session.date}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                                    {session.notes}
                                  </Typography>
                                  <Rating value={session.rating} precision={0.5} readOnly size="small" sx={{ color: '#DAA520', mt: 0.5 }} />
                                </Box>
                              </ListItem>
                              {i < 2 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      </Box>
                    )}
                    {index === 2 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                          Your Stats — Last 12 Months
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { icon: <FireIcon sx={{ color: '#8B4513', fontSize: 32 }} />, value: '47', label: 'Cigars Smoked' },
                            { icon: <StarIcon sx={{ color: '#DAA520', fontSize: 32 }} />, value: '4.2', label: 'Avg Rating' },
                            { icon: <PublicIcon sx={{ color: '#4CAF50', fontSize: 32 }} />, value: '12', label: 'Countries' },
                            { icon: <TrophyIcon sx={{ color: '#DAA520', fontSize: 32 }} />, value: '$840', label: 'Collection Value' }
                          ].map((stat, i) => (
                            <Grid item xs={6} key={i}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  borderRadius: 2,
                                  backgroundColor: 'white',
                                  border: '1px solid rgba(139,69,19,0.1)'
                                }}
                              >
                                {stat.icon}
                                <Typography variant="h5" fontWeight={700} sx={{ color: '#3d1f0a', lineHeight: 1.2, mt: 0.5 }}>
                                  {stat.value}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {stat.label}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Container>
      </Box>

      {/* Additional Features Grid */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Additional Features
          </Typography>
          <Grid container spacing={4}>
            {additionalFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#8B4513', fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits by User Type */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Perfect for Every Type of User
          </Typography>
          <Grid container spacing={6}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', p: 4 }}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ color: '#8B4513', fontWeight: 600, mb: 3 }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mb: 3, color: 'text.secondary' }}
                    >
                      {benefit.description}
                    </Typography>
                    <List>
                      {benefit.points.map((point, pointIndex) => (
                        <ListItem key={pointIndex} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={point}
                            primaryTypographyProps={{
                              variant: 'body2'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to Experience All These Features?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Start your free trial today and discover why real cigar enthusiasts 
              choose CigarTrack for their collection management needs.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: 'white',
                color: '#8B4513',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Start Your Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default FeaturesPage

